import { FileDown } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMensalidades } from '@/context/MensalidadesContext';

/**
 * Drop zone full-screen — overlay aparece ao dragover na window inteira.
 * Aceita .xlsx via drop. PROMPT_DASHBOARD §5.2: "Drag & drop do .xlsx para
 * qualquer parte da janela".
 *
 * Mantem um contador para tratar dragenter/leave repetidos (children events
 * disparam leave do parent — o contador resolve flicker).
 */
export function DropZone() {
  const { loadFromFile } = useMensalidades();
  const { t } = useTranslation();
  const [isDragging, setIsDragging] = useState(false);

  useEffect(() => {
    let dragCounter = 0;

    const onDragEnter = (e: DragEvent) => {
      e.preventDefault();
      if (e.dataTransfer?.types?.includes('Files')) {
        dragCounter++;
        setIsDragging(true);
      }
    };

    const onDragLeave = (e: DragEvent) => {
      e.preventDefault();
      dragCounter--;
      if (dragCounter <= 0) {
        dragCounter = 0;
        setIsDragging(false);
      }
    };

    const onDragOver = (e: DragEvent) => {
      e.preventDefault();
      if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy';
    };

    const onDrop = (e: DragEvent) => {
      e.preventDefault();
      dragCounter = 0;
      setIsDragging(false);
      const file = e.dataTransfer?.files?.[0];
      if (file) void loadFromFile(file, 'drop');
    };

    window.addEventListener('dragenter', onDragEnter);
    window.addEventListener('dragleave', onDragLeave);
    window.addEventListener('dragover', onDragOver);
    window.addEventListener('drop', onDrop);
    return () => {
      window.removeEventListener('dragenter', onDragEnter);
      window.removeEventListener('dragleave', onDragLeave);
      window.removeEventListener('dragover', onDragOver);
      window.removeEventListener('drop', onDrop);
    };
  }, [loadFromFile]);

  if (!isDragging) return null;

  return (
    <div
      className="aiobi-dropzone"
      role="region"
      aria-label={t('dropzone.title')}
      aria-live="polite"
    >
      <div className="aiobi-dropzone__inner">
        <FileDown size={64} strokeWidth={1.5} />
        <h2>{t('dropzone.title')}</h2>
        <p>{t('dropzone.subtitle')}</p>
      </div>
    </div>
  );
}
